import React, { useMemo, useState } from "react";
import {
  Bell,
  ChevronDown,
  CircleAlert,
  CircleUserRound,
  Loader2,
  MessageCircle,
  PencilLine,
  Plus,
  Trash2,
  X,
} from "lucide-react";

import MealChatbot from "./MealChatbot";
import {
  useAddMeal,
  useAthleteTrackingDay,
  useDeleteMeal,
  useUpdateMeal,
} from "../../hooks/queries/useAthleteData";
import { useToday } from "../../hooks/useToday";
import { getFormattedDate } from "../../utils/formatDate";
import { formatDateKey } from "../../utils/localDate";
import { getTodayMeals } from "../../utils/todayMeals";
import { buildStatusSummary } from "../../utils/nutritionStatus";

// Calorie estimation is hidden until it is rebuilt on a real food database —
// see docs/roadmap/meal-nutrition.md. The old flow saved meals with no
// calories whenever the description changed after estimating, so the numbers
// it showed could not be trusted. Meals are logged without calories for now.

const MEAL_TYPE_OPTIONS = [
  "Desayuno",
  "Almuerzo",
  "Cena",
  "Pre-entreno",
  "Post-entreno",
  "Snack",
];

const MEAL_TIME_OPTIONS = [
  "6:00 AM",
  "7:00 AM",
  "8:00 AM",
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
  "8:00 PM",
  "9:00 PM",
];

const INITIAL_MEAL_FORM = {
  description: "",
  time: "",
  satiety: "",
  mealType: "",
};

const MealRegisterModal = ({
  isOpen,
  isEditing,
  formData,
  errorMessage,
  isSaving,
  onChange,
  onClose,
  onSave,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white px-5 py-8 shadow-2xl sm:rounded-2xl sm:px-10 sm:py-9">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-6 top-6 text-[#111827] transition-colors hover:text-[#203c79]"
        >
          <X size={22} />
        </button>

        <div className="mx-auto max-w-[510px]">
          <div className="text-center">
            <h2 className="text-[28px] font-light uppercase leading-none tracking-tight text-[#111827]">
              {isEditing ? "Editar comida" : "Añadir comida"}
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-5 text-[#202636]">
              Registra qué comiste, a qué hora y qué tan satisfecho quedaste.
            </p>
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <input
                type="text"
                value={formData.description}
                onChange={(event) =>
                  onChange("description", event.target.value)
                }
                placeholder="Descripcion del plato*"
                className="w-full border border-[#c3c8d0] px-4 py-3 text-sm text-[#111827] outline-none placeholder:text-[#9aa3b2] focus:border-[#203c79]"
              />
              <p className="mt-1 text-[11px] leading-4 text-[#9aa3b2]">
                Ej: Pechuga de pollo a la plancha con una taza de arroz blanco y
                ensalada verde
              </p>
            </div>

            <div className="relative">
              <select
                value={formData.time}
                onChange={(event) => onChange("time", event.target.value)}
                className="w-full appearance-none border border-[#c3c8d0] bg-white px-4 py-3 pr-11 text-sm text-[#111827] outline-none focus:border-[#203c79]"
              >
                <option value="">Hora de la ingesta*</option>
                {MEAL_TIME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#111827]"
              />
            </div>

            <div>
              <p className="text-sm text-[#202636]">Nivel de saciedad</p>
              <p className="text-[11px] text-[#9aa3b2]">
                ¿Cómo te sientes después de comer?
              </p>
              <div className="mt-2 grid grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label
                    key={value}
                    className="flex items-center gap-2 text-sm text-[#111827]"
                  >
                    <input
                      type="checkbox"
                      checked={formData.satiety === String(value)}
                      onChange={() => onChange("satiety", String(value))}
                      className="h-4 w-4 accent-[#203c79]"
                    />
                    {value}
                  </label>
                ))}
              </div>
            </div>

            <div className="relative">
              <select
                value={formData.mealType}
                onChange={(event) => onChange("mealType", event.target.value)}
                className="w-full appearance-none border border-[#c3c8d0] bg-white px-4 py-3 pr-11 text-sm text-[#111827] outline-none focus:border-[#203c79]"
              >
                <option value="">Tipo de comida*</option>
                {MEAL_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#111827]"
              />
            </div>
          </div>

          {errorMessage && (
            <div className="mt-5 border border-[#f3c3b7] bg-[#fff4f1] px-4 py-3 text-sm text-[#9f2d17]">
              {errorMessage}
            </div>
          )}

          <div className="mt-6 flex flex-row items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#050b17] px-7 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-white transition-colors hover:bg-[#111827]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="inline-flex flex-1 items-center justify-center gap-2 bg-[#203c79] px-7 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-white transition-colors hover:bg-[#1a3266] disabled:cursor-not-allowed disabled:bg-[#7b8bac]"
            >
              {isSaving ? (
                <>
                  Guardando
                  <Loader2 size={14} className="animate-spin" />
                </>
              ) : isEditing ? (
                "Guardar cambios"
              ) : (
                "Guardar comida"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NutritionPage = ({
  athleteData,
  onNotificationsClick,
  onOpenStatusUpdate,
  onOpenProfile,
}) => {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  // null = closed; otherwise the day the meal belongs to and, when editing, its id.
  const [mealModal, setMealModal] = useState(null);
  const [mealForm, setMealForm] = useState(INITIAL_MEAL_FORM);
  const [mealError, setMealError] = useState("");
  const [isSavingMeal, setIsSavingMeal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const addMeal = useAddMeal();
  const updateMeal = useUpdateMeal();
  const deleteMeal = useDeleteMeal();

  const athleteName = athleteData?.full_name || "Andres Castro";
  const today = useToday();
  const dayQuery = useAthleteTrackingDay(athleteData?.id, today);
  const todayMetrics = dayQuery.data?.metrics;
  const meals = useMemo(() => getTodayMeals(todayMetrics?.meals), [todayMetrics]);

  const planItems = useMemo(
    () =>
      meals.map((meal) => ({
        key: meal.key,
        time: meal.time,
        title: meal.meal,
        description: meal.description,
      })),
    [meals],
  );

  // Estado shows today's check-in only. Otherwise say when the last one was.
  const circumference = 2 * Math.PI * 62;
  const todayStatus = todayMetrics?.dailyStatus?.latest;
  const statusSummary = useMemo(
    () => buildStatusSummary(todayStatus),
    [todayStatus],
  );
  const lastStatusDate = athleteData?.athlete_tracking?.[0]?.dates?.dailyStatus;
  const noStatusMessage =
    lastStatusDate && lastStatusDate !== today
      ? `Sin datos hoy. Tu último registro fue el ${formatDateKey(lastStatusDate)}.`
      : "Completa tu primera actualizacion para ver tu estado.";
  const dashOffset =
    circumference - (statusSummary.percentage / 100) * circumference;

  const handleMealFormChange = (field, value) => {
    setMealForm((previous) => ({
      ...previous,
      [field]: value,
    }));
    setMealError("");
  };

  const openAddMeal = () => {
    setMealForm(INITIAL_MEAL_FORM);
    setMealError("");
    setMealModal({ date: today, mealId: null });
  };

  const openEditMeal = (meal) => {
    setMealForm({
      description: meal.description,
      time: meal.time,
      satiety: meal.satiety ? String(meal.satiety) : "",
      mealType: meal.meal,
    });
    setMealError("");
    setPendingDeleteId(null);
    setMealModal({ date: today, mealId: meal.id });
  };

  const handleMealModalClose = () => {
    if (isSavingMeal) return;
    setMealModal(null);
    setMealForm(INITIAL_MEAL_FORM);
    setMealError("");
  };

  const handleMealSave = async () => {
    if (
      !mealForm.description.trim() ||
      !mealForm.time ||
      !mealForm.satiety ||
      !mealForm.mealType
    ) {
      setMealError("Completa todos los campos para guardar la comida.");
      return;
    }

    if (!athleteData?.id || !mealModal) {
      setMealError("No encontramos el atleta para guardar la comida.");
      return;
    }

    const meal = {
      meal: mealForm.mealType,
      description: mealForm.description.trim(),
      time: mealForm.time,
      satiety: Number(mealForm.satiety),
    };

    setIsSavingMeal(true);
    setMealError("");

    try {
      // Only this meal is sent; the server adds it to (or edits it in) the
      // day's row, so meals saved from another device are kept.
      if (mealModal.mealId) {
        await updateMeal.mutateAsync({
          date: mealModal.date,
          mealId: mealModal.mealId,
          changes: meal,
        });
      } else {
        await addMeal.mutateAsync({ date: mealModal.date, meal });
      }
      setMealModal(null);
      setMealForm(INITIAL_MEAL_FORM);
    } catch (error) {
      setMealError(
        error?.message || "No se pudo guardar la comida. Intenta de nuevo.",
      );
    } finally {
      setIsSavingMeal(false);
    }
  };

  const handleMealDelete = async (mealId) => {
    setDeleteError("");
    try {
      await deleteMeal.mutateAsync({ date: today, mealId });
      setPendingDeleteId(null);
    } catch (error) {
      setDeleteError(
        error?.message || "No se pudo eliminar la comida. Intenta de nuevo.",
      );
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#f4f5f8]">
      <div className="mx-auto flex max-w-7xl gap-5 px-4 py-6 sm:px-6 lg:px-7 lg:py-7">
        <div className="min-w-0 flex-1">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-600">{getFormattedDate()}</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-[#121826] sm:text-[28px]">
                Bienvenida de vuelta, {athleteName}
              </h1>
            </div>

            {/* Sin lupa: no tenía handler, así que era un botón que no hacía
                nada. Un control muerto es peor que ninguno. */}
            <div className="hidden items-center gap-3 pt-1 text-[#111827] lg:flex">
              <button
                onClick={onNotificationsClick}
                className="rounded-full p-2 transition-colors hover:bg-white"
              >
                <Bell size={20} />
              </button>
              <button
                type="button"
                onClick={onOpenProfile}
                className="rounded-full p-2 transition-colors hover:bg-white"
              >
                <CircleUserRound size={20} />
              </button>
            </div>
          </div>

          {/* La barra lateral con el chat solo aparece desde xl; sin esto el
              chat no se podía abrir en móvil ni en tablet. */}
          <button
            type="button"
            onClick={() => setIsChatbotOpen(true)}
            className="mb-5 flex w-full items-center justify-center gap-2 bg-[#203c79] px-4 py-3 text-xs font-semibold uppercase tracking-[0.45em] text-white transition-colors hover:bg-[#1a3266] sm:w-auto sm:px-6 xl:hidden"
          >
            <MessageCircle size={14} />
            Chat con IA
          </button>

          <section className="rounded-sm border border-[#d6dbe4] bg-white px-5 py-4 shadow-[0_8px_20px_rgba(15,23,42,0.08)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-[26px] leading-none text-[#252b37]">
                Tus comidas del dia
              </h2>
              <button
                type="button"
                onClick={openAddMeal}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-[#24417f] transition-colors hover:bg-[#eef3ff]"
              >
                <Plus size={16} />
                Añadir comida
              </button>
            </div>

            {dayQuery.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-6 text-sm text-[#667085]">
                <Loader2 size={16} className="animate-spin" />
                Cargando tus comidas…
              </div>
            ) : dayQuery.isError ? (
              <div className="flex flex-wrap items-center gap-3 border border-[#f3c3b7] bg-[#fff4f1] px-4 py-3 text-sm text-[#9f2d17]">
                No pudimos cargar tus comidas.
                <button
                  type="button"
                  onClick={() => dayQuery.refetch()}
                  className="font-semibold underline"
                >
                  Reintentar
                </button>
              </div>
            ) : meals.length === 0 ? (
              <div className="border border-dashed border-[#cfd6e2] bg-[#f8fafc] px-4 py-6 text-center text-sm leading-5 text-[#667085]">
                Aún no registras comidas hoy.
              </div>
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {meals.map((meal) => (
                  <li
                    key={meal.key}
                    className="flex flex-col gap-2 border border-[#e3e7ee] px-3 py-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xl font-semibold text-[#24417f]">
                        {meal.meal}
                      </p>
                      {meal.id ? (
                        <div className="flex shrink-0 items-center gap-1 text-[#667085]">
                          <button
                            type="button"
                            onClick={() => openEditMeal(meal)}
                            aria-label={`Editar ${meal.meal}`}
                            className="rounded-full p-1.5 transition-colors hover:bg-[#eef3ff] hover:text-[#24417f]"
                          >
                            <PencilLine size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteError("");
                              setPendingDeleteId(meal.id);
                            }}
                            aria-label={`Eliminar ${meal.meal}`}
                            className="rounded-full p-1.5 transition-colors hover:bg-[#fff4f1] hover:text-[#9f2d17]"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {meal.description ? (
                      <p className="text-xs leading-4 text-[#4b5563]">
                        {meal.description}
                        {meal.time ? (
                          <span className="text-[#9aa3b2]"> · {meal.time}</span>
                        ) : null}
                      </p>
                    ) : null}
                    {pendingDeleteId === meal.id ? (
                      <div className="flex flex-wrap items-center gap-2 text-xs text-[#202636]">
                        ¿Eliminar esta comida?
                        <button
                          type="button"
                          onClick={() => handleMealDelete(meal.id)}
                          disabled={deleteMeal.isPending}
                          className="inline-flex items-center gap-1 bg-[#9f2d17] px-2.5 py-1 font-semibold text-white disabled:opacity-60"
                        >
                          {deleteMeal.isPending ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : null}
                          Eliminar
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeleteId(null)}
                          className="px-2 py-1 font-medium text-[#475467]"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {deleteError ? (
              <p className="mt-3 text-sm text-[#9f2d17]">{deleteError}</p>
            ) : null}
          </section>

          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[0.9fr_1.8fr]">
            <section className="rounded-sm border border-[#d6dbe4] bg-white p-5">
              <h2 className="text-[26px] leading-none text-[#252b37]">
                Estado
              </h2>
              <p className="mt-5 min-h-[48px] text-sm leading-5 text-[#202636]">
                {statusSummary.summary}
              </p>

              <div className="mt-7 flex min-h-[176px] justify-center">
                {statusSummary.hasData ? (
                  <div className="relative h-48 w-48">
                    <svg className="h-48 w-48 -rotate-90" viewBox="0 0 192 192">
                      <circle
                        cx="96"
                        cy="96"
                        r="62"
                        fill="none"
                        stroke={statusSummary.trackColor}
                        strokeWidth="14"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="62"
                        fill="none"
                        stroke={statusSummary.accentColor}
                        strokeWidth="14"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                      <span className="text-3xl font-semibold text-[#111827]">
                        {statusSummary.percentage}%
                      </span>
                      <span className="mt-1 max-w-[92px] text-[10px] font-medium leading-[1.2] text-[#667085]">
                        {statusSummary.label}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-44 w-full items-center justify-center border border-dashed border-[#cfd6e2] bg-[#f8fafc] px-5 text-center text-sm leading-5 text-[#667085]">
                    {noStatusMessage}
                  </div>
                )}
              </div>

              <button
                onClick={onOpenStatusUpdate}
                className="mt-4 w-full bg-[#203c79] px-4 py-3 text-xs font-semibold uppercase tracking-[0.4em] text-white transition-colors hover:bg-[#1a3266]"
              >
                Actualizar estado
              </button>
            </section>

            <section className="rounded-sm border border-[#d6dbe4] bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-[26px] leading-none text-[#252b37]">
                  Recomendaciones para hoy
                </h2>
                <CircleAlert size={18} className="mt-1 text-[#ff5f3a]" />
              </div>

              <div className="mt-5 space-y-5">
                {statusSummary.recommendations.length > 0 ? (
                  statusSummary.recommendations.map((recommendation) => (
                    <article key={recommendation.title}>
                      <h3 className="text-base font-semibold text-[#111827]">
                        {recommendation.title}
                      </h3>
                      <p className="mt-1 text-sm leading-5 text-[#4b5563]">
                        {recommendation.body}
                      </p>
                      <span
                        className="mt-2 inline-flex rounded-full px-3 py-1 text-sm"
                        style={{
                          backgroundColor: statusSummary.badgeBg,
                          color: statusSummary.badgeText,
                        }}
                      >
                        {recommendation.badge}
                      </span>
                    </article>
                  ))
                ) : (
                  <div className="flex min-h-[220px] items-center justify-center border border-dashed border-[#cfd6e2] bg-[#f8fafc] px-6 text-center text-sm leading-5 text-[#667085]">
                    Las recomendaciones aparecerán cuando registres tu estado
                    de hoy.
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <aside className="hidden w-[320px] shrink-0 border border-[#d6dbe4] bg-white px-4 py-4 xl:block">
          <h2 className="text-[26px] leading-none text-[#252b37]">
            Plan de comidas
          </h2>
          <button
            onClick={() => setIsChatbotOpen(true)}
            className="mt-5 flex w-full items-center justify-center gap-2 bg-[#203c79] px-4 py-3 text-xs font-semibold uppercase tracking-[0.45em] text-white transition-colors hover:bg-[#1a3266]"
          >
            <MessageCircle size={14} />
            Chat con IA
          </button>

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-[#111827]">
              Recomendaciones de hoy
            </h3>

            <div className="mt-4 space-y-0">
              {planItems.length > 0 ? (
                planItems.map((item, index) => (
                  <div
                    key={item.key}
                    className={`grid grid-cols-[72px_1fr] gap-3 border-[#cfd6e2] py-3 ${index < planItems.length - 1 ? "border-b" : ""}`}
                  >
                    <div className="h-[46px] rounded-xl bg-[#b7c8ea]" />
                    <div>
                      <p className="text-sm font-semibold text-[#111827]">
                        {item.time}
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-[#24417f]">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-xs leading-4 text-[#6b7280]">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="border border-dashed border-[#cfd6e2] bg-[#f8fafc] px-4 py-8 text-center text-sm leading-5 text-[#667085]">
                  El plan de comidas aparecera cuando exista informacion
                  nutricional registrada.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <MealChatbot
        isOpen={isChatbotOpen}
        onClose={() => setIsChatbotOpen(false)}
      />
      <MealRegisterModal
        isOpen={Boolean(mealModal)}
        isEditing={Boolean(mealModal?.mealId)}
        formData={mealForm}
        errorMessage={mealError}
        isSaving={isSavingMeal}
        onChange={handleMealFormChange}
        onClose={handleMealModalClose}
        onSave={handleMealSave}
      />
    </div>
  );
};

export default NutritionPage;
